/**
 * CompanionOnboardingModal.tsx
 * Main orchestrator component for the Companion AI onboarding flow.
 *
 * Flow:
 * 1. Intro screen → user clicks "Start"
 * 2. Steps 1-6 → user fills preferences, clicks "Next"
 * 3. Success screen → user clicks "Start Chatting"
 *
 * Features:
 * - Progress bar showing current step (1-6 of 6)
 * - Form validation gates progression
 * - Error display for failed submissions
 * - Loading state during submission
 * - Scroll indicator and extra padding for Step 4 (dropdown)
 */
import React from "react";
import { View, StyleSheet, ActivityIndicator, ScrollView } from "react-native";
import CustomModal from "../../common/general/CustomModal/CustomModal";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import OnboardingIntro from "./OnboardingIntro";
import OnboardingStep1 from "./OnboardingStep1";
import OnboardingStep2 from "./OnboardingStep2";
import OnboardingStep3 from "./OnboardingStep3";
import OnboardingStep4 from "./OnboardingStep4";
import OnboardingStep5 from "./OnboardingStep5";
import OnboardingStep6 from "./OnboardingStep6";
import OnboardingSuccess from "./OnboardingSuccess";
import { colors, spacing, radii } from "../../../styles/design-system";
import { CompanionOnboardingScreen, type CompanionOnboardingFormData } from "../../../hooks/useCompanionOnboardingForm";
import type { UseFormSetValue } from "react-hook-form";

// Helper function to convert screen enum to step number for progress bar
const getStepNumber = (screen: CompanionOnboardingScreen): number => {
  switch (screen) {
    case CompanionOnboardingScreen.Step_1:
      return 1;
    case CompanionOnboardingScreen.Step_2:
      return 2;
    case CompanionOnboardingScreen.Step_3:
      return 3;
    case CompanionOnboardingScreen.Step_4:
      return 4;
    case CompanionOnboardingScreen.Step_5:
      return 5;
    case CompanionOnboardingScreen.Step_6:
      return 6;
    default:
      return 0;
  }
};

export interface CompanionOnboardingModalProps {
  visible: boolean;
  currentScreen: CompanionOnboardingScreen;
  formData: CompanionOnboardingFormData;
  setValue: UseFormSetValue<CompanionOnboardingFormData>;
  onStart: () => void;
  onNext: () => void;
  onBack: () => void;
  onSubmit: () => void;
  onSkip: () => void;
  onComplete: () => void;
  canProceed: boolean;
  isLoading: boolean;
  error?: string;
  goalOptions: { key: string; value: string }[];
  setPhoneVerified: (verified: boolean) => void;
  setSmsOptIn: (optIn: boolean) => void;
  isCompanionMode?: boolean;
}

function CompanionOnboardingModal({
  visible,
  currentScreen,
  formData,
  setValue,
  onStart,
  onNext,
  onBack,
  onSubmit,
  onSkip,
  onComplete,
  canProceed,
  isLoading,
  error,
  goalOptions,
  setPhoneVerified,
  setSmsOptIn,
  isCompanionMode = true,
}: CompanionOnboardingModalProps) {
  const totalSteps = 6;
  const currentStep = getStepNumber(currentScreen);
  const isFirstStep = currentStep === 1;
  const isFinalStep = currentStep === totalSteps;
  const showSkipLink = currentScreen === CompanionOnboardingScreen.Step_1;

  const renderContent = () => {
    // Render different screens based on currentScreen state
    switch (currentScreen) {
      case CompanionOnboardingScreen.Intro:
        return <OnboardingIntro onStart={onStart} onSkip={onSkip} isCompanionMode={isCompanionMode} />;

      case CompanionOnboardingScreen.Success:
        return <OnboardingSuccess onComplete={onComplete} isCompanionMode={isCompanionMode} />;

      case CompanionOnboardingScreen.Step_1:
        return (
          <OnboardingStep1
            selectedCareerPaths={formData.careerInterests ?? []}
            onCareerPathsChange={(value) => setValue("careerInterests", value)}
          />
        );

      case CompanionOnboardingScreen.Step_2:
        return (
          <OnboardingStep2
            selectedContinuingEducation={formData.continuingEducation ?? []}
            onContinuingEducationChange={(value) => setValue("continuingEducation", value)}
          />
        );

      case CompanionOnboardingScreen.Step_3:
        return (
          <OnboardingStep3
            selectedInterests={formData.interests ?? []}
            onInterestsChange={(value) => setValue("interests", value)}
          />
        );

      case CompanionOnboardingScreen.Step_4:
        return (
          <OnboardingStep4
            selectedSkills={formData.skillsToImprove ?? []}
            onSkillsChange={(value) => setValue("skillsToImprove", value)}
            selectedLearningMethods={formData.learningStyles ?? []}
            onLearningMethodsChange={(value) => setValue("learningStyles", value)}
          />
        );

      case CompanionOnboardingScreen.Step_5:
        return (
          <OnboardingStep5
            selectedMotivations={formData.motivations ?? []}
            onMotivationsChange={(value) => setValue("motivations", value)}
            selectedGoal={formData.semesterGoal ?? ""}
            onGoalChange={(value) => setValue("semesterGoal", value)}
            goalOptions={goalOptions}
          />
        );

      case CompanionOnboardingScreen.Step_6:
        return (
          <OnboardingStep6
            assignmentReminders={formData.assignmentReminders ?? true}
            onAssignmentRemindersChange={(value) => setValue("assignmentReminders", value)}
            facultyUpdates={formData.facultyEnabled ?? true}
            onFacultyUpdatesChange={(value) => setValue("facultyEnabled", value)}
            studyTime={formData.studyTimePreference ?? ""}
            onStudyTimeChange={(value) => setValue("studyTimePreference", value)}
            checkInFrequency={formData.checkInFrequency ?? ""}
            onCheckInFrequencyChange={(value) => setValue("checkInFrequency", value)}
            wantTextReminders={formData.textReminders ?? false}
            onWantTextRemindersChange={(value) => setValue("textReminders", value)}
            onPhoneVerifiedChange={setPhoneVerified}
            onSmsOptInChange={setSmsOptIn}
          />
        );

      default:
        return null;
    }
  };

  const isStepScreen =
    currentScreen !== CompanionOnboardingScreen.Intro && currentScreen !== CompanionOnboardingScreen.Success;
  const showProgressBar = isStepScreen;
  const showFooter = isStepScreen;

  const isSuccessScreen = currentScreen === CompanionOnboardingScreen.Success;

  return (
    <CustomModal
      visible={visible}
      onRequestClose={() => {}}
      testID="companion-onboarding-modal"
      style={[styles.modal, isSuccessScreen && styles.modalWide]}
      bodyStyle={styles.modalBody}
      ariaLabel="companion_onboarding_modal_label">
      <View style={styles.container}>
        {showProgressBar && (
          <View style={styles.header}>
            <CustomText
              text="companion_onboarding_progress"
              interpolation={{ current: currentStep, total: totalSteps }}
              size="s"
              weight="medium"
              style={styles.progress}
            />
            <View style={styles.progressBar}>
              {Array.from({ length: totalSteps }).map((_, index) => (
                <View
                  key={index}
                  style={[styles.progressSegment, index + 1 <= currentStep && styles.progressSegmentFilled]}
                />
              ))}
            </View>
          </View>
        )}

        <ScrollView
          style={styles.content}
          contentContainerStyle={[
            styles.contentContainer,
            currentStep === 5 && styles.contentContainerStep4, // Extra padding for What Drives You dropdown
          ]}
          showsVerticalScrollIndicator={true}>
          {renderContent()}
        </ScrollView>

        {error && (
          <View style={styles.errorContainer}>
            <CustomText text={error} size="m" style={styles.errorText} useTranslationText={false} />
          </View>
        )}

        {showFooter && (
          <View style={styles.footer}>
            {/* On Step 1, show "Skip Later" link to skip entire onboarding */}
            {showSkipLink && (
              <CustomButton
                text="buttons.companion_onboarding_intro_skip"
                onPress={onSkip}
                styleType="secondaryLightened"
                style={styles.skipLink}
              />
            )}
            {/* On Steps 2-5, show "Back" button (disabled on Step 1) */}
            {!showSkipLink && (
              <CustomButton
                text="buttons.companion_onboarding_back"
                onPress={onBack}
                disabled={isFirstStep || isLoading}
                styleType="secondaryLightened"
                style={styles.backButton}
              />
            )}
            {/* Main CTA: "Next" on Steps 1-5, "Submit" on Step 6 */}
            {/* Disabled if form validation fails (canProceed=false) or submission in progress */}
            <CustomButton
              text={isFinalStep ? "buttons.companion_onboarding_submit" : "buttons.companion_onboarding_next"}
              onPress={isFinalStep ? onSubmit : onNext}
              disabled={!canProceed || isLoading}
              styleType="primary"
              style={styles.nextButton}>
              {isLoading && <ActivityIndicator size="small" color="#fff" style={styles.loader} />}
            </CustomButton>
          </View>
        )}
      </View>
    </CustomModal>
  );
}

const styles = StyleSheet.create({
  modal: {
    borderRadius: radii.xl,
    padding: 0,
    // @ts-expect-error - rem units work on web
    maxWidth: "36rem",
    width: "100%",
    // @ts-expect-error - vh units work on web
    maxHeight: "92vh",
    border: "none",
    outline: "none",
    boxShadow: "none",
  },
  modalWide: {
    // @ts-expect-error - rem units work on web
    maxWidth: "56rem",
  },
  modalBody: {
    padding: 0,
    margin: 0,
  },
  container: {
    flex: 1,
    minHeight: 400,
  },
  header: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.xl,
    paddingBottom: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  progress: {
    color: colors.grey[600],
    marginBottom: spacing.sm,
  },
  progressBar: {
    flexDirection: "row",
    gap: spacing.xs,
    height: 4,
  },
  progressSegment: {
    flex: 1,
    height: 4,
    backgroundColor: colors.grey[200],
    borderRadius: 2,
  },
  progressSegmentFilled: {
    backgroundColor: colors.brand[500],
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.lg,
  },
  // Step 4 has a dropdown (goal selector) that may extend beyond visible area
  // Extra padding (80px) ensures dropdown options aren't hidden behind footer buttons
  contentContainerStep4: {
    paddingBottom: spacing.xl + spacing.lg + 80,
  },
  errorContainer: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    backgroundColor: colors.error[50],
    borderTopWidth: 1,
    borderTopColor: colors.error[200],
  },
  errorText: {
    color: colors.error[600],
  },
  footer: {
    flexDirection: "row",
    gap: spacing.md,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  skipLink: {
    flex: 1,
  },
  backButton: {
    flex: 1,
  },
  nextButton: {
    flex: 2,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  loader: {
    marginLeft: spacing.sm,
  },
});

export default CompanionOnboardingModal;
